import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SITE from '../api';
import { usePortal, PORTAL } from '../portalAuth';
import { useSite, IMG } from '../components/StoreLayout';
import { STORE_BASE } from '../routeBase';
import { ProductCard } from './Home';
import { TestimonialScroller } from './AboutPage';
import StorePhoneInput from '../components/StorePhoneInput';
import Captcha from '../components/Captcha';
import useSEO from '../useSEO';
import { trackEvent } from '../analytics';

function ytVideoId(url) {
  if (!url) return '';
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{6,})/);
  return m ? m[1] : '';
}

const LENS_TYPES = [
  { key: 'Single Vision Lenses', label: 'Single Vision' },
  { key: 'Bi-Focal Lenses', label: 'Bi-Focal' },
  { key: 'Progressive Lenses', label: 'Progressive' },
  { key: 'Blue Block Lenses', label: 'Blue Block' },
];

const EMPTY_FORM = {
  name: '', email: '', phone: '', lens_type: '', quantity: '', message: '',
  od_sph: '', od_cyl: '', od_axis: '', od_add: '',
  os_sph: '', os_cyl: '', os_axis: '', os_add: '', pd: '',
};

export default function StockRxLenses() {
  const { content: c } = useSite();
  const portal = usePortal();
  const customer = portal?.customer;
  const [activeType, setActiveType] = useState(LENS_TYPES[0].key);
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [orderType, setOrderType] = useState('stock');
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    // Logged-in customers get priced rows from the portal API (same pattern
    // as Home.js/Shop.js) — the public SITE endpoint never includes price.
    const client = customer ? PORTAL : SITE;
    client.get('/products', { params: { category: activeType, limit: 8 } }).then(r => setProducts(r.data.products || []));
  }, [customer, activeType]);

  useEffect(() => {
    SITE.get('/testimonials', { params: { page: 'stock-rx-lenses' } }).then(r => setTestimonials(r.data.testimonials || []));
  }, []);

  useEffect(() => {
    let slugs = [];
    try { slugs = JSON.parse(c['page.stock-rx-lenses.featured_blogs'] || '[]'); } catch { /* ignore malformed content */ }
    if (!slugs.length) { setFeaturedBlogs([]); return; }
    SITE.get('/blogs').then(r => {
      const bySlug = new Map((r.data.blogs || []).map(b => [b.slug, b]));
      setFeaturedBlogs(slugs.map(s => bySlug.get(s)).filter(Boolean));
    });
  }, [c]);

  useSEO({
    title: c['page.stock-rx-lenses.meta_title'] || c['page.stock-rx-lenses.title'] || 'Stock & Rx Lenses',
    description: c['page.stock-rx-lenses.meta_description'],
    canonical: `${window.location.origin}/store/page/stock-rx-lenses`,
  });

  let benefits = [];
  try { benefits = JSON.parse(c['page.stock-rx-lenses.benefits'] || '[]'); } catch { /* ignore malformed content */ }
  let faq = [];
  try { faq = JSON.parse(c['page.stock-rx-lenses.faq'] || '[]'); } catch { /* ignore malformed content */ }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault(); setErr('');
    if (!form.name || !form.email) { setErr('Please enter your name and email'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setErr('Please enter a valid email address');
    if (!form.phone || !form.phone.trim()) return setErr('Please enter your phone number');
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append('order_type', orderType);
      fd.append('captcha_token', captchaToken);
      if (file) fd.append('attachment', file);
      await axios.post('/api/stock-rx-lenses/enquiry', fd);
      setSent(true);
      trackEvent('wholesale_enquiry', { source: 'stock-rx-lenses', order_type: orderType });
    } catch (e2) { setErr(e2.response?.data?.error || 'Could not submit. Please try again.'); }
    setBusy(false);
  };

  const heroImg = c['page.stock-rx-lenses.hero_image'];
  const videoId = ytVideoId(c['page.stock-rx-lenses.video_url']);

  return (
    <div>
      <style>{`
        .lens-type-card { transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease; }
        .lens-type-card:hover { transform: translateY(-3px); box-shadow: 0 10px 26px rgba(0,0,0,0.07); border-color: var(--sf-accent, #85cde9) !important; }
        .lens-tab { border: 1.5px solid #e2e2e2; background: #fff; color: #555; padding: 9px 20px; border-radius: 22px; font-size: 13px; font-weight: 600; cursor: pointer; }
        .lens-tab.active { border-color: var(--sf-primary, #191919); background: var(--sf-primary, #191919); color: #fff; }
        .lens-order-toggle { border: 1.5px solid #e2e2e2; background: #fff; color: #555; padding: 11px 22px; border-radius: 8px; font-size: 13.5px; font-weight: 600; cursor: pointer; flex: 1; }
        .lens-order-toggle.active { border-color: var(--sf-primary, #191919); background: var(--sf-primary, #191919); color: #fff; }
        .lens-input { width:100%; padding:7px 9px; border:1.5px solid #e5e5e5; border-radius:6px; font-size:13px; box-sizing:border-box; text-align:center; }
        .lens-model-grid { display: flex; flex-wrap: wrap; justify-content: center; align-items: stretch; gap: 24px; }
        .lens-model-card { width: 240px; flex: 0 0 auto; display: flex; }
        .lens-model-card > div { display: flex; flex-direction: column; width: 100%; }
        .lens-model-card > div > a { display: flex; flex-direction: column; flex: 1; }
        .lens-model-card > div > a > div { display: flex; flex-direction: column; flex: 1; }
        .lens-model-card > div > a > div > div:last-child { flex: 1; }
      `}</style>

      {/* HERO */}
      <section className="sf-fade-in" style={{
        position: 'relative', minHeight: 340, display: 'flex', alignItems: 'center', overflow: 'hidden',
        // Matches the 1600x700 "best size" hint given in the admin editor —
        // without this, a fluid-width section with only a minHeight floor
        // rarely has the same aspect ratio as the uploaded image, so
        // background-size:cover silently crops its top/bottom to fill
        // whatever height the box ends up with.
        ...(heroImg ? { aspectRatio: '1600 / 700' } : {}),
        background: heroImg
          ? `linear-gradient(180deg, rgba(10,20,30,.55), rgba(10,20,30,.75)), url(${IMG(heroImg)}) center/cover`
          : 'linear-gradient(135deg, var(--sf-primary, #16324a) 0%, #2c5a7a 100%)',
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '56px 24px', textAlign: 'center', color: '#fff', width: '100%', boxSizing: 'border-box' }}>
          <span style={{ display: 'inline-block', background: 'rgba(255,255,255,0.16)', padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, letterSpacing: 0.5, marginBottom: 16 }}>
            SINGLE VISION · BI-FOCAL · PROGRESSIVE · BLUE BLOCK
          </span>
          <h1 style={{ fontSize: 'clamp(28px,4vw,42px)', fontWeight: 800, lineHeight: 1.15, margin: '0 0 14px' }}>
            {c['page.stock-rx-lenses.title'] || 'Stock & Rx Lenses'}
          </h1>
          <p style={{ fontSize: 15.5, opacity: 0.92, maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
            {c['page.stock-rx-lenses.subtitle'] || 'Ready stock for fast turnaround, or custom-ground to any prescription — one place to order both.'}
          </p>
          <a href="#lens-form" style={{ display: 'inline-block', marginTop: 26, border: '1.5px solid #fff', color: '#fff', padding: '12px 30px', borderRadius: 26, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
            Place An Order
          </a>
        </div>
      </section>

      {/* INTRO / USAGE */}
      {c['page.stock-rx-lenses'] && (
        <section style={{ maxWidth: 760, margin: '48px auto 0', padding: '0 24px', textAlign: 'center' }}>
          <div className="sf-richtext" style={{ fontSize: 15, lineHeight: 1.8, color: '#444' }} dangerouslySetInnerHTML={{ __html: c['page.stock-rx-lenses'] }} />
        </section>
      )}

      {/* LENS TYPES */}
      {benefits.length > 0 && (
        <section style={{ maxWidth: 1100, margin: '40px auto 0', padding: '0 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
            {benefits.map((b, i) => (
              <div key={i} className="lens-type-card" style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: '26px 22px', textAlign: 'center' }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'color-mix(in srgb, var(--sf-accent, #85cde9) 25%, white)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                  <i className={`ti ti-${b.icon}`} style={{ fontSize: 24, color: 'var(--sf-primary, #191919)' }} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>{b.title}</h3>
                <p style={{ fontSize: 13, color: '#666', lineHeight: 1.6, margin: 0 }}>{b.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* LENS COLLECTION */}
      <section style={{ maxWidth: 1300, margin: '56px auto 0', padding: '0 24px' }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, textAlign: 'center', marginBottom: 8 }}>Our Lens Collection</h2>
        {c['page.stock-rx-lenses.models_intro'] && (
          <p style={{ textAlign: 'center', color: '#777', fontSize: 14, maxWidth: 620, margin: '0 auto 24px' }}>{c['page.stock-rx-lenses.models_intro']}</p>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 28 }}>
          {LENS_TYPES.map(t => (
            <button key={t.key} className={`lens-tab${activeType === t.key ? ' active' : ''}`} onClick={() => setActiveType(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        {products.length > 0 ? (
          <>
            <div className="lens-model-grid">
              {products.map(p => (
                <div key={p.model_no} className="lens-model-card">
                  <ProductCard p={p} />
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <Link to={`${STORE_BASE}/shop?category=${encodeURIComponent(activeType)}`} className="sf-btn-outline"
                style={{ display: 'inline-block', padding: '11px 28px', borderRadius: 24, fontWeight: 700, fontSize: 13, textDecoration: 'none', border: '1.5px solid var(--sf-primary)', color: 'var(--sf-primary)' }}>
                View All {LENS_TYPES.find(t => t.key === activeType)?.label} Lenses
              </Link>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '30px 20px', background: '#f7f8fa', borderRadius: 14, color: '#888' }}>Loading…</div>
        )}
      </section>

      {/* VIDEO */}
      {videoId && (
        <section style={{ maxWidth: 900, margin: '56px auto 0', padding: '0 24px' }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, textAlign: 'center', marginBottom: 20 }}>See How Our Lenses Are Made</h2>
          <div style={{ width: '100%', maxWidth: 720, height: 0, paddingTop: '56.25%', margin: '0 auto', position: 'relative', borderRadius: 14, overflow: 'hidden', background: '#000' }}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=1&rel=0&modestbranding=1`}
              title="Stock & Rx Lenses"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
              allow="autoplay; encrypted-media" allowFullScreen
            />
          </div>
        </section>
      )}

      {/* ORDER & PRESCRIPTION FORM */}
      <section id="lens-form" style={{ maxWidth: 720, margin: '56px auto 0', padding: '0 24px' }}>
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 16, padding: 30 }}>
          {sent ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <i className="ti ti-circle-check" style={{ fontSize: 44, color: '#16a34a' }} />
              <h3 style={{ marginTop: 10 }}>Thank you!</h3>
              <p style={{ color: '#666', fontSize: 14 }}>Your order has been received — our team will review it and get in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Order Stock or Rx Lenses</h3>
              <p style={{ fontSize: 13.5, color: '#888', marginBottom: 18 }}>Choose ready-stock lenses for fast dispatch, or a custom prescription order — either way, we'll take it from here.</p>
              {err && <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '8px 11px', borderRadius: 8, marginBottom: 12 }}>{err}</div>}

              <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
                <button type="button" className={`lens-order-toggle${orderType === 'stock' ? ' active' : ''}`} onClick={() => setOrderType('stock')}>Stock Lens</button>
                <button type="button" className={`lens-order-toggle${orderType === 'rx' ? ' active' : ''}`} onClick={() => setOrderType('rx')}>Custom Rx Lens</button>
              </div>

              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <FormField label="Name *" value={form.name} onChange={set('name')} required />
                <FormField label="Email *" type="email" value={form.email} onChange={set('email')} required />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Phone *</label>
                <StorePhoneInput value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} required />
              </div>

              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Lens Type</label>
                  <select className="lens-input" style={{ textAlign: 'left' }} value={form.lens_type} onChange={set('lens_type')}>
                    <option value="">Select lens type…</option>
                    {LENS_TYPES.map(t => <option key={t.key} value={t.label}>{t.label}</option>)}
                    <option value="Not sure / Other">Not sure / Other</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Quantity</label>
                  <input className="lens-input" style={{ textAlign: 'left' }} value={form.quantity} onChange={set('quantity')} placeholder="e.g. 50 pairs" />
                </div>
              </div>

              {orderType === 'rx' && (
                <>
                  <h4 style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 10 }}>Lens Prescription (optional if attaching a scan)</h4>
                  <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
                      <thead>
                        <tr>{['', 'SPH', 'CYL', 'AXIS', 'ADD'].map(h => <th key={h} style={{ fontSize: 11, color: '#888', padding: '4px 6px', textAlign: 'center' }}>{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        <RxRow label="OD (right)" prefix="od" form={form} set={set} />
                        <RxRow label="OS (left)" prefix="os" form={form} set={set} />
                      </tbody>
                    </table>
                  </div>
                  <div style={{ maxWidth: 160, marginBottom: 16 }}>
                    <label style={lbl}>PD (mm)</label>
                    <input className="lens-input" value={form.pd} onChange={set('pd')} />
                  </div>
                </>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Attach a prescription or reference file (photo or PDF, optional)</label>
                <input type="file" accept="image/*,application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} />
                {file && <div style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>✓ {file.name}</div>}
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Message</label>
                <textarea value={form.message} onChange={set('message')} rows={3}
                  style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
              </div>

              <Captcha onToken={setCaptchaToken} />
              <button type="submit" disabled={busy} className="sf-btn-outline"
                style={{ width: '100%', padding: 12, border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}>
                {busy ? 'Submitting…' : 'Submit Order'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section style={{ maxWidth: 1100, margin: '56px auto 0', padding: '0 24px' }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, textAlign: 'center', marginBottom: 24 }}>What Our Customers Say</h2>
          <TestimonialScroller items={testimonials} />
        </section>
      )}

      {/* FAQ */}
      {faq.length > 0 && (
        <section style={{ maxWidth: 800, margin: '56px auto 0', padding: '0 24px' }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, textAlign: 'center', marginBottom: 20 }}>Frequently Asked Questions</h2>
          {faq.map((f, i) => (
            <details key={i} style={{ borderTop: '1px solid #eee', padding: '12px 0' }}>
              <summary style={{ fontSize: 14.5, fontWeight: 600, cursor: 'pointer', color: 'var(--sf-primary)' }}>{f.question}</summary>
              <p style={{ fontSize: 13.5, color: '#666', lineHeight: 1.7, marginTop: 8 }}>{f.answer}</p>
            </details>
          ))}
        </section>
      )}

      {/* BLOG */}
      <section style={{ maxWidth: 1100, margin: '56px auto 70px', padding: '0 24px' }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, textAlign: 'center', marginBottom: 24 }}>Lens &amp; Eyecare Guides</h2>
        {featuredBlogs.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20 }}>
            {featuredBlogs.map(b => (
              <Link key={b.slug} to={`${STORE_BASE}/blog/${b.slug}`} style={{ textDecoration: 'none', color: 'inherit', width: 280, flex: '0 0 auto' }}>
                <div className="sf-journal-card" style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, overflow: 'hidden', height: '100%' }}>
                  <div style={{ height: 150, background: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {b.cover_image ? <img src={IMG(b.cover_image)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <i className="ti ti-article" style={{ fontSize: 36, color: '#ddd' }} />}
                  </div>
                  <div style={{ padding: 14 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, lineHeight: 1.35 }}>{b.title}</h3>
                    <p style={{ fontSize: 12.5, color: '#777', lineHeight: 1.6 }}>{b.excerpt}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '30px 20px', background: '#f7f8fa', borderRadius: 14, color: '#888' }}>
            <i className="ti ti-notes" style={{ fontSize: 28, marginBottom: 8, display: 'block' }} />
            More lens &amp; eyecare guides coming soon.
          </div>
        )}
      </section>
    </div>
  );
}

function RxRow({ label, prefix, form, set }) {
  return (
    <tr>
      <td style={{ fontSize: 12.5, fontWeight: 600, padding: '4px 6px' }}>{label}</td>
      {['sph', 'cyl', 'axis', 'add'].map(f => (
        <td key={f} style={{ padding: '4px 6px' }}>
          <input className="lens-input" value={form[`${prefix}_${f}`]} onChange={set(`${prefix}_${f}`)} />
        </td>
      ))}
    </tr>
  );
}

function FormField({ label, value, onChange, type = 'text', required }) {
  return (
    <div style={{ flex: 1 }}>
      <label style={lbl}>{label}</label>
      <input type={type} required={required} value={value} onChange={onChange} style={{ width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
    </div>
  );
}

const lbl = { fontSize: 12.5, fontWeight: 500, color: '#555', display: 'block', marginBottom: 5 };
